import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Camera, Upload, X, Video } from "lucide-react";
import { uploadFile, fileUrl } from "@/lib/api";
import { isVideoPath } from "@/components/MediaViewer";
import { toast } from "sonner";

const MAX_MB = 50;

export default function PhotoUpload({ value, onChange, testId = "photo", label = "Upload photo or video", accept = "image/*,video/*", compact = false }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const videoOnly = accept === "video/*";

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) { toast.error(`File too large (max ${MAX_MB}MB)`); return; }
    if (videoOnly && !file.type.startsWith("video/")) { toast.error("Please choose a video"); return; }
    if (accept === "image/*" && !file.type.startsWith("image/")) { toast.error("Please choose a photo"); return; }
    setBusy(true);
    try {
      const path = await uploadFile(file);
      onChange(path);
      toast.success(file.type.startsWith("video/") ? "Video uploaded" : "Photo uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const h = compact ? "h-24" : "h-40";
  if (value) {
    const video = isVideoPath(value);
    return (
      <div className={`relative rounded-lg overflow-hidden border border-emerald-500/50 bg-black ${h}`} data-testid={`${testId}-preview`}>
        {video ? (
          <video src={fileUrl(value)} controls playsInline className="w-full h-full object-contain" />
        ) : (
          <img src={fileUrl(value)} alt="upload" className="w-full h-full object-cover" />
        )}
        {label && <span className="absolute bottom-1 left-1 text-[9px] uppercase tracking-widest bg-black/70 px-1.5 py-0.5 rounded text-white">{label}</span>}
        <button
          type="button"
          onClick={() => onChange(null)}
          className="absolute top-1.5 right-1.5 bg-black/70 rounded-full p-1.5 hover:bg-black"
          data-testid={`${testId}-clear`}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={pick}
        data-testid={`${testId}-input`}
      />
      <Button
        type="button"
        variant="outline"
        className={`w-full ${compact ? "h-24 flex-col gap-1 px-2" : "h-24"} border-dashed border-2 border-[#28364F] bg-[#131B2A] hover:bg-[#1C263B] text-[#94A3B8]`}
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        data-testid={`${testId}-btn`}
      >
        {busy ? <Upload className="w-5 h-5 animate-pulse" /> : videoOnly ? <Video className="w-5 h-5" /> : accept === "image/*" ? <Camera className="w-5 h-5" /> : (<><Camera className="w-5 h-5" /><Video className="w-5 h-5 ml-1" /></>)}
        <span className={compact ? "text-[11px] leading-tight whitespace-normal text-center" : "ml-2"}>{busy ? "Uploading..." : label}</span>
      </Button>
    </div>
  );
}
